import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CreateIcon from "@material-ui/icons/Create";

import { dateTimeZoneConverter } from "../../../../../../utils/commonFunctions/date";
import useQuerySearch from "../../../../../../hooks/querySearch/useQuerySearch";
import { IFootcareDevice } from "../../../../../../@types/device.interface";
import { DataGrid, DataGridColumn, GridAction } from "../../../../../shared";

/** The API returns the joined group and store under hyphenated keys. */
type DeviceRow = IFootcareDevice & {
	"group-details"?: { group_name?: string };
	"store-details"?: { store_name?: string };
	createdAt?: string;
};

const DeviceManagementGrid = ({ gridData }: { gridData: IFootcareDevice[] }): JSX.Element => {
	const groupNumber = useQuerySearch("gn");
	const navigate = useNavigate();

	const routeToEdit = useCallback(
		(deviceId: string) => {
			navigate(
				groupNumber
					? `/admin/settings/edit-device/${deviceId}?gn=${groupNumber}`
					: `/settings/edit-device/${deviceId}`
			);
		},
		[navigate, groupNumber]
	);

	const columns = useMemo<DataGridColumn<DeviceRow>[]>(
		() => [
			{ key: "device_id", header: "Device ID", width: "12rem" },
			{ key: "group_name", header: "Group name", render: (row) => row["group-details"]?.group_name ?? "—" },
			{ key: "store_name", header: "Store name", render: (row) => row["store-details"]?.store_name ?? "—" },
			{
				key: "createdAt",
				header: "Assigned date",
				render: (row) => (row.createdAt ? (dateTimeZoneConverter(row.createdAt) as string) : "N/A")
			},
			{
				key: "action",
				header: "Edit",
				width: "6rem",
				align: "center",
				stopRowClick: true,
				render: (row) => (
					<GridAction icon title="Edit device" onClick={() => routeToEdit(row.device_id)}>
						<CreateIcon fontSize="small" />
					</GridAction>
				)
			}
		],
		[routeToEdit]
	);

	return (
		<DataGrid
			rows={gridData as DeviceRow[]}
			columns={columns}
			getRowId={(row, index) => row.device_id ?? String(index)}
			emptyMessage="No devices assigned yet."
		/>
	);
};

export default DeviceManagementGrid;
